import React, { useContext } from "react";
import {
  BrowserRouter,
  Routes as RouterRoutes,
  Route,
  Navigate,
} from "react-router-dom";
import Home from "./views/Home/Home";
import Activities from "./views/Activities/Activities";
import NewActivity from "./views/Activities/NewActivity/NewActivity";
import ActivityEvaluation from "./views/Activities/ActivityEvaluation/ActivityEvaluation";
import RegisterOptions from "./views/RegisterOptions/RegisterOptions";
import Login from "./views/Login/Login";
import { RegisterProvider } from "./context/registerContext";
import ProjectSheet from "./views/ProjectSheet/ProjectSheet";
import { ProjectShettProvider } from "./context/sheetContext";
import NewSearch from "./views/NewSearch/NewSearch";
import Logout from "./components/Auth/Logout";
import Unauthorized from "./components/Auth/Unauthorized/Unauthorized";
import Auth from "./components/Auth/Auth";
import PrivateRoute from "./privateRoute";
import Research from "./views/Research/Research";
import { ActivitiesProvider } from "./context/activitiesContext";
import ThesaurusAdmin from "./admin/ThesaurusAdmin/ThesaurusAdmin";
import ListStatusActivities from "./views/ListStatusActivities/ListStatusActivities";
import LiteraryWork from "./views/LiteraryWork/LiteraryWork";
import ProjectSheetResearch from "./views/ProjectSheet/ProjectSheetResearch";
import RegisterDramaturgyAndVarieties from "./views/RegisterDramaturgyAndVarieties/RegisterDramaturgyAndVarieties";
import ProspectsAndProductions from "./views/ProspectsAndProductions/ProspectsAndProductions";
import { ProspectsAndProductionsProvider } from "./context/productionsAndProspectsContext";
import ReleasedTexts from "./views/ReleasedTexts/ReleasedTexts";
import UserContext from "./context/userContext";
import AuthGate from "./components/Auth/AuthGate";
import ProjectReleasedText from "./views/ReleasedTexts/ProjectReleasedText/ProjectReleasedText";
import ExternalProjects from "./views/ExternalProjects/ExternalProjects";
import ErrorPage from "./components/Auth/ErrorPage/ErrorPage";
import GoogleTagManager from "./services/GoogleTagManager/GoogleTagManager";
import { SharedTexts } from "./views/SharedTexts/SharedTexts";
import CultureHub from "./views/CultureHub/CultureHub";
import Maintenance from "./views/Maintenance/Maintenance";
import { getHomeRoute } from "./getHomeRoute";
import Header from "./components/Header/Header";
import { RecommendationsAPIProvider } from "./context/RecommendationsAPI/recommendationsAPIContext";
import { HotjarProvider } from "./context/HotjarContext";

const HomeRoute = () => {
  const { me } = useContext(UserContext);

  const route = getHomeRoute({
    permissions: me.user.sectionsAllowed,
    roles: me.user.roles,
  });

  if (route !== "/") return <Navigate to={route} replace />;

  return (
    <>
      <Header />
      <Home />
    </>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <GoogleTagManager>
        <HotjarProvider>
          <AuthGate>
            <RouterRoutes>
              <Route path="/auth" element={<Auth />} />
              <Route path="/logout" element={<Logout />} />
              <Route path="/acesso-negado" element={<Unauthorized />} />
              <Route path="/erro" element={<ErrorPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="*" element={null} />
            </RouterRoutes>

            <RecommendationsAPIProvider>
              <ProspectsAndProductionsProvider>
                <RouterRoutes>
                  <Route path="/" element={<HomeRoute />} />
                  <Route path="*" element={null} />
                </RouterRoutes>
              </ProspectsAndProductionsProvider>

              <RouterRoutes>
                <Route
                  path="/textos-compartilhados"
                  element={
                    <PrivateRoute requiredPermissions={["AttachmentViewer"]}>
                      <SharedTexts />
                    </PrivateRoute>
                  }
                />
                <Route path="*" element={null} />
              </RouterRoutes>

              <ProjectShettProvider>
                <RouterRoutes>
                  <Route
                    path="/hub-cultura/:id?"
                    element={
                      <PrivateRoute
                        requiredRoles={["CultureHub", "Admin"]}
                        hideHeader
                      >
                        <CultureHub />
                      </PrivateRoute>
                    }
                  />
                  <Route path="*" element={null} />
                </RouterRoutes>
              </ProjectShettProvider>

              <RouterRoutes>
                <Route
                  path="/maintenance"
                  element={
                    <PrivateRoute requiredRoles={["Admin"]} hideHeader>
                      <Maintenance />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/projetos-externos"
                  element={
                    <PrivateRoute requiredPermissions={["ExternalProject"]}>
                      <ExternalProjects />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/admin/thesaurus"
                  element={
                    <PrivateRoute requiredPermissions={["Thesaurus"]}>
                      <ThesaurusAdmin />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/textos-liberados"
                  element={
                    <PrivateRoute requiredPermissions={["TextRelease"]}>
                      <ReleasedTexts />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/texto-liberado/:id"
                  element={
                    <PrivateRoute requiredPermissions={["TextRelease"]}>
                      <ProjectReleasedText />
                    </PrivateRoute>
                  }
                />
                <Route path="*" element={null} />
              </RouterRoutes>

              <ActivitiesProvider>
                <RouterRoutes>
                  <Route
                    path="/atividades"
                    element={
                      <PrivateRoute requiredPermissions={["Activities"]}>
                        <Activities />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/atividade/:type/:status"
                    element={
                      <PrivateRoute requiredPermissions={["Activities"]}>
                        <ListStatusActivities />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/atividades/:slug/nova-atividade"
                    element={
                      <PrivateRoute requiredPermissions={["Activities"]}>
                        <NewActivity />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/atividades/:type/:evaluation/:evaluationId"
                    element={
                      <PrivateRoute
                        requiredPermissions={["Activities"]}
                        hideHeader
                      >
                        <ActivityEvaluation />
                      </PrivateRoute>
                    }
                  />
                  <Route path="*" element={null} />
                </RouterRoutes>
              </ActivitiesProvider>

              <ProspectsAndProductionsProvider>
                <RouterRoutes>
                  <Route
                    path="/proponentes"
                    element={
                      <PrivateRoute requiredPermissions={["Prospects"]}>
                        <ProspectsAndProductions type="prospects" />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/produtoras"
                    element={
                      <PrivateRoute
                        requiredPermissions={["ProductionCompanies"]}
                      >
                        <ProspectsAndProductions type="productioncompanies" />
                      </PrivateRoute>
                    }
                  />
                  <Route path="*" element={null} />
                </RouterRoutes>

                <RegisterProvider>
                  <RouterRoutes>
                    <Route
                      path="/cadastrar"
                      element={
                        <PrivateRoute
                          requiredPermissions={[
                            "Dramaturgies",
                            "Varieties",
                            "Researches",
                            "LiteraryWorks",
                          ]}
                        >
                          <RegisterOptions />
                        </PrivateRoute>
                      }
                    />

                    <Route
                      path="/cadastrar/dramaturgia"
                      element={
                        <PrivateRoute requiredPermissions={["Dramaturgies"]}>
                          <RegisterDramaturgyAndVarieties type="dramaturgia" />
                        </PrivateRoute>
                      }
                    />

                    <Route
                      path="/cadastrar/variedades"
                      element={
                        <PrivateRoute requiredPermissions={["Varieties"]}>
                          <RegisterDramaturgyAndVarieties type="variedades" />
                        </PrivateRoute>
                      }
                    />

                    <Route
                      path="/pesquisa/cadastrar"
                      element={
                        <PrivateRoute requiredPermissions={["Researches"]}>
                          <Research />
                        </PrivateRoute>
                      }
                    />

                    <Route
                      path="/obra-literaria/cadastrar"
                      element={
                        <PrivateRoute requiredPermissions={["LiteraryWorks"]}>
                          <LiteraryWork />
                        </PrivateRoute>
                      }
                    />
                    <Route path="*" element={null} />
                  </RouterRoutes>

                  <ProjectShettProvider>
                    <RouterRoutes>
                      <Route
                        path="/ficha-projeto/:type/:id"
                        element={
                          <PrivateRoute
                            requiredPermissions={[
                              "Dramaturgies",
                              "Varieties",
                              "Researches",
                              "LiteraryWorks",
                              "ExternalProject",
                            ]}
                          >
                            <ProjectSheet />
                          </PrivateRoute>
                        }
                      />

                      <Route
                        path="/ficha-pesquisa/:id"
                        element={
                          <PrivateRoute requiredPermissions={["Researches"]}>
                            <ProjectSheetResearch />
                          </PrivateRoute>
                        }
                      />
                      <Route path="*" element={null} />
                    </RouterRoutes>
                  </ProjectShettProvider>
                </RegisterProvider>

                <ProjectShettProvider>
                  <RouterRoutes>
                    <Route
                      path="/busca"
                      element={
                        <PrivateRoute
                          requiredPermissions={[
                            "Dramaturgies",
                            "Varieties",
                            "Researches",
                            "LiteraryWorks",
                            "ExternalProject",
                          ]}
                        >
                          <NewSearch />
                        </PrivateRoute>
                      }
                    />
                    <Route path="*" element={null} />
                  </RouterRoutes>
                </ProjectShettProvider>
              </ProspectsAndProductionsProvider>
            </RecommendationsAPIProvider>
          </AuthGate>
        </HotjarProvider>
      </GoogleTagManager>
    </BrowserRouter>
  );
};

export default Routes;
